import {
  Alert,
  Badge,
  Button,
  Collapse,
  Divider,
  Group,
  Modal,
  NumberInput,
  Paper,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea
} from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { IconPlus } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { useCallback, useEffect, useMemo, useState } from "react";
import useUserStore from "@/modules/users/store";
import CriteriosList from "./CriteriosList";
import GuiaNotasEditor from "./GuiaNotasEditor";
import { useRegulamentoIdeiasFormContext } from "./regulamento-ideias-form-context";
import {
  DEFAULT_CRITERIO,
  DEFAULT_CRITERIO_ROWS,
  DEFAULT_DESCRIPTION_ROWS,
  DEFAULT_ESCALA_MIN,
  DEFAULT_PESO
} from "./rubrica-editor-utils";
import RubricaResumo from "./RubricaResumo";
import {
  REGULAMENTOS_IDEIAS_COLLECTION_KEY,
  getRubricaIdeiasByReference,
  getRubricaIdeiasDocumentReferenceFromPath,
  getRegulamentoIdeiasDocumentReference,
  TRegulamentoIdeiasForm,
  rubricaIdeiasConverter
} from "../../firestore";
import { createRubricaIdeias, gerarCriteriosAutomaticamente } from "../../functions";
import rubricaSchema, { TRubricaIdeiasInput, criterioSchema } from "../../schema/rubrica-schema";

const DEFAULT_RUBRICA_TEMPLATE: TRubricaIdeiasInput = {
  version: "",
  descricao: "",
  criterios: []
};

type RubricaEditorProperties = {
  regulamento: TRegulamentoIdeiasForm;
};

type RubricaAtivaSectionProperties = {
  activeRubrica: TRubricaIdeias | null;
};

type RubricaHistoricoSectionProperties = {
  rubricas: TRubricaIdeias[];
  opened: boolean;
  onToggle: () => void;
};

/**
 * @param mode
 */
function getRubricaModalTitle(mode: "nova" | "duplicar"): string {
  return mode === "duplicar" ? "Duplicar ativa" : "Nova";
}

/**
 * @param root0
 * @param root0.activeRubrica
 */
function RubricaAtivaSection({ activeRubrica }: RubricaAtivaSectionProperties): JSX.Element {
  if (activeRubrica) {
    return <RubricaResumo rubrica={activeRubrica} titulo="Ativa" />;
  }

  return (
    <Paper withBorder p="md">
      <Text size="sm" c="dimmed">
        Nenhuma rubrica cadastrada.
      </Text>
    </Paper>
  );
}

/**
 * @param root0
 * @param root0.rubricas
 * @param root0.opened
 * @param root0.onToggle
 */
function RubricaHistoricoSection({
  rubricas,
  opened,
  onToggle
}: RubricaHistoricoSectionProperties): JSX.Element | null {
  if (rubricas.length === 0) {
    return null;
  }

  const toggleLabel = opened ? "Ocultar versoes anteriores" : "Ver versoes anteriores";

  return (
    <>
      <Button variant="transparent" size="xs" onClick={onToggle}>
        {toggleLabel}
      </Button>
      <Collapse in={opened}>
        <Stack gap="xs">
          {rubricas.map((item) => (
            <RubricaResumo key={item.refPath} rubrica={item} titulo="Versao anterior" />
          ))}
        </Stack>
      </Collapse>
    </>
  );
}

/**
 * @param root0
 * @param root0.regulamento
 */
function RubricaEditor({ regulamento }: RubricaEditorProperties): JSX.Element {
  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);
  const activeLicenciadaId = useUserStore((state) => state.activeLicenciadaId);
  const queryClient = useQueryClient();
  const regulamentoForm = useRegulamentoIdeiasFormContext();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeRubricaReference, setActiveRubricaReference] = useState(regulamento.rubricaAtivaRef ?? null);
  const [criterioModalOpened, setCriterioModalOpened] = useState(false);
  const [criterioEditIndex, setCriterioEditIndex] = useState<number | null>(null);
  const [rubricaModalOpened, setRubricaModalOpened] = useState(false);
  const [historyOpened, setHistoryOpened] = useState(false);
  const [rubricaModalMode, setRubricaModalMode] = useState<"nova" | "duplicar">("nova");
  const [isGenerating, setIsGenerating] = useState(false);

  const rubricaForm = useForm<TRubricaIdeiasInput>({
    initialValues: DEFAULT_RUBRICA_TEMPLATE,
    validate: yupResolver(rubricaSchema)
  });

  const criterioForm = useForm<TCriterioRubrica>({
    initialValues: DEFAULT_CRITERIO,
    validate: yupResolver(criterioSchema)
  });

  useEffect(() => {
    setActiveRubricaReference(regulamento.rubricaAtivaRef ?? null);
  }, [regulamento.rubricaAtivaRef]);

  const activeRubricaPath = activeRubricaReference?.path ?? null;

  const { data: rubrica } = useQuery<TRubricaIdeias | null, Error>({
    queryKey: [REGULAMENTOS_IDEIAS_COLLECTION_KEY, "rubrica", activeRubricaPath],
    queryFn: async () => {
      if (!activeRubricaReference) {
        return null;
      }
      const snapshot = await getRubricaIdeiasByReference(activeRubricaReference);
      return snapshot.exists() ? snapshot.data() : null;
    },
    enabled: Boolean(activeRubricaReference)
  });

  const { data: rubricasHistorico = [] } = useQuery<TRubricaIdeias[], Error>({
    queryKey: [REGULAMENTOS_IDEIAS_COLLECTION_KEY, "rubricas", activeEmpresaId, activeLicenciadaId, regulamento.id],
    queryFn: async () => {
      if (!activeEmpresaId || !activeLicenciadaId || !regulamento.id) {
        return [];
      }
      const regulamentoReference = getRegulamentoIdeiasDocumentReference(
        activeEmpresaId,
        regulamento.id,
        activeLicenciadaId
      );
      const rubricasCollection = collection(regulamentoReference, "rubricas").withConverter(rubricaIdeiasConverter);
      const rubricasQuery = query(rubricasCollection, orderBy("createdAt", "desc"));
      const snapshot = await getDocs(rubricasQuery);
      return snapshot.docs.map((document_) => document_.data());
    },
    enabled: Boolean(activeEmpresaId && activeLicenciadaId && regulamento.id)
  });

  const activeRubrica = useMemo(() => {
    if (rubrica) {
      return rubrica;
    }
    if (activeRubricaPath) {
      const rubricaFromPath = rubricasHistorico.find((item) => item.refPath === activeRubricaPath);
      if (rubricaFromPath) {
        return rubricaFromPath;
      }
    }
    if (regulamento.rubricaAtivaVersion) {
      return rubricasHistorico.find((item) => item.version === regulamento.rubricaAtivaVersion) ?? null;
    }
    return null;
  }, [activeRubricaPath, regulamento.rubricaAtivaVersion, rubrica, rubricasHistorico]);

  // Sincroniza formulário quando rubrica ativa é carregada ou alterada
  useEffect(() => {
    if (!activeRubrica) {
      return;
    }

    // Evita atualizações desnecessárias se os valores já são idênticos
    const valuesAreIdentical =
      rubricaForm.values.version === activeRubrica.version &&
      rubricaForm.values.descricao === activeRubrica.descricao &&
      rubricaForm.values.criterios.length === activeRubrica.criterios.length;

    if (valuesAreIdentical) {
      return;
    }

    rubricaForm.setValues({
      version: activeRubrica.version,
      descricao: activeRubrica.descricao,
      criterios: activeRubrica.criterios
    });
    // Nota: rubricaForm não está nas dependências para evitar loops infinitos
    // rubricaForm.setValues é estável (garantido pelo Mantine)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRubrica]);

  const rubricaLabel = useMemo(() => {
    if (activeRubrica?.version) {
      return `ativa: ${activeRubrica.version}`;
    }
    if (regulamento.rubricaAtivaVersion) {
      return `ativa: ${regulamento.rubricaAtivaVersion}`;
    }
    return "Nenhuma Matriz de Critérios ativa";
  }, [activeRubrica?.version, regulamento.rubricaAtivaVersion]);

  const activeRubricaReferencePath = activeRubrica?.refPath ?? activeRubricaPath;
  const rubricasAnteriores = rubricasHistorico.filter((item) => item.refPath !== activeRubricaReferencePath);
  const hasActiveRubrica = Boolean(activeRubrica);
  const canCreateRubrica = Boolean(regulamento.id && activeEmpresaId && activeLicenciadaId);

  const { mutate, isPending } = useMutation({
    mutationFn: async (values: TRubricaIdeiasInput) => {
      if (!activeEmpresaId || !activeLicenciadaId) {
        throw new Error("Empresa ou licenciada nao selecionada");
      }
      if (!regulamento.id) {
        throw new Error("Regulamento precisa ser salvo antes de criar rubrica");
      }
      const result = await createRubricaIdeias({
        licenciadaId: activeLicenciadaId,
        empresaId: activeEmpresaId,
        regulamentoIdeiasId: regulamento.id,
        rubrica: values
      });
      return result.data;
    },
    onSuccess(data) {
      showNotification({ message: "Rubrica salva com sucesso", color: "green", autoClose: 2500 });
      setErrorMessage(null);
      if (data?.rubricaPath) {
        const rubricaReference = getRubricaIdeiasDocumentReferenceFromPath(data.rubricaPath);
        setActiveRubricaReference(rubricaReference);
        regulamentoForm.setFieldValue("rubricaAtivaRef", rubricaReference);
        regulamentoForm.setFieldValue("rubricaAtivaVersion", data.rubricaVersion);
      }
      void queryClient.invalidateQueries({ queryKey: [...REGULAMENTOS_IDEIAS_COLLECTION_KEY] });
      setRubricaModalOpened(false);
    },
    onError(error) {
      const errorText = error instanceof Error ? error.message : "Erro ao salvar rubrica";
      setErrorMessage(errorText);
    }
  });

  /**
   *
   */
  function handleSaveRubrica(): void {
    const validation = rubricaForm.validate();
    if (validation.hasErrors) {
      setErrorMessage("Revise os campos obrigatorios da rubrica");
      return;
    }
    mutate(rubricaForm.values);
  }

  /**
   * Abre modal de rubrica para criar nova ou duplicar existente
   * @param mode - "nova" para criar rubrica vazia, "duplicar" para copiar rubrica ativa
   */
  const openRubricaModal = useCallback(
    (mode: "nova" | "duplicar"): void => {
      setRubricaModalMode(mode);
      setErrorMessage(null);
      if (mode === "duplicar" && activeRubrica) {
        // Copia explicitamente os valores da rubrica ativa
        rubricaForm.setValues({
          version: activeRubrica.version,
          descricao: activeRubrica.descricao,
          criterios: activeRubrica.criterios
        });
      } else {
        // Limpa o form para nova rubrica
        rubricaForm.setValues(DEFAULT_RUBRICA_TEMPLATE);
      }
      setRubricaModalOpened(true);
      // Nota: rubricaForm.setValues é estável (garantido pelo Mantine)
      // Apenas rubrica precisa estar nas dependências/**
    },
    [activeRubrica]
  );

  /**
   *
   * @param criterio
   * @param index
   */
  function openCriterioModal(criterio?: TCriterioRubrica, index?: number): void {
    setCriterioEditIndex(typeof index === "number" ? index : null);
    criterioForm.setValues(criterio ?? DEFAULT_CRITERIO);
    setCriterioModalOpened(true);
  }

  /**
   *
   */
  function handleSaveCriterio(): void {
    const validation = criterioForm.validate();
    if (validation.hasErrors) {
      return;
    }

    if (criterioEditIndex === null) {
      rubricaForm.insertListItem("criterios", criterioForm.values);
    } else {
      rubricaForm.setFieldValue(`criterios.${criterioEditIndex}`, criterioForm.values);
    }

    setCriterioModalOpened(false);
    criterioForm.setValues(DEFAULT_CRITERIO);
    setCriterioEditIndex(null);
  }

  /**
   *
   * @param index
   */
  function handleRemoveCriterio(index: number): void {
    rubricaForm.removeListItem("criterios", index);
  }

  // Stable handlers to avoid inline function creation
  const handleOpenNovaRubrica = useCallback(() => {
    openRubricaModal("nova");
  }, [openRubricaModal]);

  const handleOpenDuplicarRubrica = useCallback(() => {
    openRubricaModal("duplicar");
  }, [openRubricaModal]);

  const handleToggleHistory = useCallback(() => {
    setHistoryOpened((opened) => !opened);
  }, []);

  const handleCloseRubricaModal = useCallback(() => {
    setRubricaModalOpened(false);
  }, []);

  const handleCloseCriterioModal = useCallback(() => {
    setCriterioModalOpened(false);
  }, []);

  const handleOpenCriterioModal = useCallback(() => {
    openCriterioModal();
  }, []);

  const handleGuiaNotasChange = useCallback(
    (value: Record<string, string>) => {
      criterioForm.setFieldValue("guiaNotas", value);
    },
    [criterioForm]
  );

  const handleGerarAutomaticamente = useCallback(async () => {
    if (!regulamento.objetivosPrograma || !activeEmpresaId || !activeLicenciadaId || !regulamento.id) {
      showNotification({
        message: "Preencha os objetivos do programa antes de gerar automaticamente",
        color: "orange"
      });
      return;
    }

    setIsGenerating(true);
    setErrorMessage(null);

    try {
      const result = await gerarCriteriosAutomaticamente({
        licenciadaId: activeLicenciadaId,
        empresaId: activeEmpresaId,
        regulamentoIdeiasId: regulamento.id,
        objetivosPrograma: regulamento.objetivosPrograma,
        nomePrograma: regulamento.nome
      });

      // Preencher formulário com resultado da IA
      rubricaForm.setValues({
        version: result.data.version,
        descricao: result.data.descricao,
        criterios: result.data.criterios
      });

      // Abrir modal para revisão
      setRubricaModalMode("nova");
      setRubricaModalOpened(true);

      showNotification({
        message: "Critérios gerados com sucesso! Revise e ajuste antes de salvar.",
        color: "green"
      });
    } catch (error) {
      const errorText = error instanceof Error ? error.message : "Erro ao gerar critérios automaticamente";
      setErrorMessage(errorText);
      showNotification({
        message: errorText,
        color: "red"
      });
    } finally {
      setIsGenerating(false);
    }
  }, [
    regulamento.objetivosPrograma,
    regulamento.id,
    regulamento.nome,
    activeEmpresaId,
    activeLicenciadaId,
    rubricaForm
  ]);

  return (
    <>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <Text fw={600}>Matriz de Critérios</Text>
            <Badge variant="light" color={activeRubrica?.version ? "blue" : "gray"}>
              {rubricaLabel}
            </Badge>
          </Group>
          <Group gap="xs">
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={handleGerarAutomaticamente}
              disabled={!regulamento.objetivosPrograma || !canCreateRubrica}
              loading={isGenerating}
            >
              Gerar Automaticamente
            </Button>
            <Button
              size="xs"
              variant="outline"
              leftSection={<IconPlus size={14} />}
              onClick={handleOpenNovaRubrica}
              disabled={!canCreateRubrica}
            >
              Nova
            </Button>
            <Button
              size="xs"
              variant="default"
              leftSection={<IconPlus size={14} />}
              onClick={handleOpenDuplicarRubrica}
              disabled={!canCreateRubrica || !hasActiveRubrica}
            >
              Duplicar ativa
            </Button>
          </Group>
        </Group>

        {!canCreateRubrica && (
          <Text size="sm" c="dimmed">
            Salve o regulamento para cadastrar rubricas.
          </Text>
        )}

        <RubricaAtivaSection activeRubrica={activeRubrica} />

        <RubricaHistoricoSection rubricas={rubricasAnteriores} opened={historyOpened} onToggle={handleToggleHistory} />
      </Stack>

      <Modal
        opened={rubricaModalOpened}
        onClose={handleCloseRubricaModal}
        title={getRubricaModalTitle(rubricaModalMode)}
        size="xl"
        closeOnClickOutside={false}
      >
        <Stack>
          <TextInput label="Versao" placeholder="Ex: v1" {...rubricaForm.getInputProps("version")} />
          {rubricaModalMode === "duplicar" && (
            <Text size="xs" c="dimmed">
              Atualize a versao para identificar a nova Matriz de Critérios.
            </Text>
          )}
          <Textarea label="Descricao" minRows={DEFAULT_DESCRIPTION_ROWS} {...rubricaForm.getInputProps("descricao")} />

          <Divider />

          <CriteriosList
            criterios={rubricaForm.values.criterios}
            onAdd={handleOpenCriterioModal}
            onEdit={openCriterioModal}
            onRemove={handleRemoveCriterio}
            error={rubricaForm.errors.criterios as string | undefined}
          />

          {errorMessage && (
            <Alert color="red" title="Erro">
              {errorMessage}
            </Alert>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={handleCloseRubricaModal}>
              Cancelar
            </Button>
            <Button loading={isPending} onClick={handleSaveRubrica}>
              Salvar rubrica
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={criterioModalOpened}
        onClose={handleCloseCriterioModal}
        title={criterioEditIndex === null ? "Novo criterio" : "Editar criterio"}
        size="lg"
        closeOnClickOutside={false}
      >
        <Stack>
          <TextInput label="ID" placeholder="Ex: impacto" {...criterioForm.getInputProps("id")} />
          <TextInput label="Nome" {...criterioForm.getInputProps("nome")} />
          <Textarea label="Descricao" minRows={DEFAULT_CRITERIO_ROWS} {...criterioForm.getInputProps("descricao")} />
          <Group grow>
            <NumberInput label="Peso" min={DEFAULT_PESO} {...criterioForm.getInputProps("peso")} />
            <NumberInput label="Escala min" min={DEFAULT_ESCALA_MIN} {...criterioForm.getInputProps("escalaMin")} />
            <NumberInput label="Escala max" min={DEFAULT_ESCALA_MIN} {...criterioForm.getInputProps("escalaMax")} />
          </Group>
          <Switch label="Obrigatorio" {...criterioForm.getInputProps("obrigatorio", { type: "checkbox" })} />
          <GuiaNotasEditor
            value={criterioForm.values.guiaNotas}
            escalaMin={criterioForm.values.escalaMin}
            escalaMax={criterioForm.values.escalaMax}
            onChange={handleGuiaNotasChange}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={handleCloseCriterioModal}>
              Cancelar
            </Button>
            <Button onClick={handleSaveCriterio}>Salvar criterio</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

export default RubricaEditor;
